import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { useMemo, useState, useRef, useEffect, memo } from "react";
import { Bar } from "react-chartjs-2";
import { useSelector, shallowEqual } from "react-redux";
import { useColorMode, Box, Flex, Text, Button } from "theme-ui";
import { FaCalendarAlt } from 'react-icons/fa';
import { DateRangeSelect } from "../../../../forms-component/dateRangeSelect";
import { ProjectManagerData } from "../../../../../types/dashboard.types";
import GenericChartSkeleton from "../../skeleton/GenericChartSkeleton";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// Bar Colors for sales representative
const COLORS = [
    "#6366f1",
    "#8b5cf6",
    "#ec4899",
    "#f43f5e",
    "#f97316",
    "#eab308",
    "#22c55e",
    "#06b6d4",
    "#3b82f6",
];

const dataLabelPlugin = {
    id: 'dataLabelPlugin',
    afterDatasetsDraw(chart: { ctx: CanvasRenderingContext2D; data: { datasets: any[] }; getDatasetMeta: (i: number) => any }) {
        const { ctx } = chart;
        chart.data.datasets.forEach((dataset: any, i: number) => {
            const isDark = dataset.isDarkMode !== false;

            const meta = chart.getDatasetMeta(i);
            meta.data.forEach((bar: any, index: number) => {
                const value = dataset.data[index];
                if (value === null || value === undefined) return;
                if (bar.hidden) return;

                const x = bar.x;
                const y = bar.y - 15;

                let barColor = '#ffffff';

                if (!isDark) {
                    if (bar.options && bar.options.backgroundColor) {
                        barColor = bar.options.backgroundColor;
                    } else if (Array.isArray(dataset.backgroundColor)) {
                        barColor = dataset.backgroundColor[index % dataset.backgroundColor.length];
                    } else if (typeof dataset.backgroundColor === 'string') {
                        barColor = dataset.backgroundColor;
                    } else {
                        barColor = '#000000';
                    }
                }

                ctx.fillStyle = barColor;
                ctx.font = 'bold 11px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(String(value), x, y);
            });
        });
    }
};

const ActiveProjectsSalesRepChart = memo(({ dateRange, setDateRange }: { dateRange: string, setDateRange: (range: string) => void }) => {
    const { UserSalesRepresentativeData, allDataLoading, UserSalesRepresentativeLoading, chartLoading } = useSelector((state: any) => ({
        UserSalesRepresentativeData: state.dashboardData.UserSalesRepresentativeData,
        allDataLoading: state.dashboardData.allDataLoading,
        UserSalesRepresentativeLoading: state.dashboardData.UserSalesRepresentativeLoading,
        chartLoading: state.dashboardData.chartLoading
    }), shallowEqual);

    const [colorMode] = useColorMode();
    const isDark = colorMode === 'dark';
    const graphlabelColor = colorMode === 'light' ? "#453521" : "#dadada";
    const borderColor = colorMode === 'light' ? "#e5e7eb" : "#2e354d";

    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const calendarRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
                setIsCalendarOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const topChartData = useMemo(() => {
        let data: ProjectManagerData[] = [...(UserSalesRepresentativeData || [])];
        data.sort((a, b) => b.count - a.count);

        return {
            labels: data.map((item: ProjectManagerData) => {
                const parts = item.name.trim().split(/\s+/);
                if (parts.length > 1) {
                    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
                }
                return item.name;
            }),
            datasets: [
                {
                    label: "Active Projects",
                    data: data.map((item: ProjectManagerData) => item.count),
                    backgroundColor: COLORS,
                    borderRadius: { topLeft: 5, topRight: 5 },
                    barThickness: 50,
                    isDarkMode: isDark,
                },
            ],
        };
    }, [UserSalesRepresentativeData, isDark]);

    const chartMinWidth = topChartData.labels.length * 120;

    const maxVal = Math.max(...(topChartData.datasets[0].data as number[]), 0);
    const yAxisMax = maxVal + Math.ceil(maxVal * 0.15) + 1;

    const topChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: false },
            tooltip: {
                backgroundColor: isDark ? '#20283c' : '#ffffff',
                titleColor: isDark ? '#ffffff' : '#20283c',
                bodyColor: isDark ? '#cbd5e1' : '#475569',
                borderColor: borderColor,
                borderWidth: 1,
                padding: 12,
                boxPadding: 4,
                usePointStyle: true,
                callbacks: {
                    label: (context: any) => ` Projects: ${context.raw}`
                }
            }
        },
        scales: {
            x: {
                ticks: {
                    color: graphlabelColor,
                    maxRotation: 0,
                    minRotation: 0,
                    autoSkip: false
                },
                grid: { display: false }
            },
            y: {
                beginAtZero: true,
                suggestedMax: yAxisMax,
                ticks: { color: graphlabelColor },
                grid: { color: colorMode === 'dark' ? '#2e354d' : '#eee' }
            }
        }
    };


    const cardBg = colorMode === 'light' ? "#ffffff" : "#2e354db8";
    const titleColor = colorMode === 'light' ? "#453521" : "#ffffff";

    const isLoading = allDataLoading || UserSalesRepresentativeLoading || chartLoading['all'] || chartLoading['salesRepresentative'];

    return (
        <Box sx={{ bg: cardBg, p: 3, borderRadius: 8 }}>
            <Flex sx={{ justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                <Text sx={{ color: titleColor, fontSize: 3, fontWeight: 600 }}>Active Projects Per Sales Representative (Real-Time)</Text>
                <Flex sx={{ alignItems: 'center', gap: 2 }}>
                    {dateRange && (
                        <Button
                            onClick={() => setDateRange("")}
                            sx={{
                                bg: 'transparent',
                                color: 'primary',
                                border: '1px solid',
                                borderColor: 'primary',
                                px: 2,
                                py: 0,
                                fontSize: 0,
                                height: '24px',
                                cursor: 'pointer',
                                borderRadius: 4,
                                '&:hover': { bg: 'primary', color: 'white' }
                            }}
                        >
                            Clear
                        </Button>
                    )}
                    <Box sx={{ position: 'relative' }} ref={calendarRef}>
                        <Box
                            sx={{
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                p: 1,
                                borderRadius: '4px',
                                '&:hover': { bg: isLoading ? 'transparent' : isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' },
                                display: 'flex',
                                alignItems: 'center',
                                opacity: isLoading ? 0.5 : 1
                            }}
                            onClick={() => {
                                if (!isLoading) {
                                    setIsCalendarOpen(!isCalendarOpen);
                                }
                            }}
                        >
                            <FaCalendarAlt size={18} color={titleColor} />
                        </Box>
                        {isCalendarOpen && (
                            <Box sx={{ position: 'absolute', top: '100%', right: 0, zIndex: 10, mt: 1, bg: isDark ? 'background' : 'white', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
                                <DateRangeSelect
                                    onChange={(value: any) => {
                                        if (value && value.includes(" to ")) {
                                            setDateRange(value);
                                            setIsCalendarOpen(false);
                                        }
                                    }}
                                    values={dateRange}
                                />
                            </Box>
                        )}
                    </Box>
                </Flex>
            </Flex>
            <Box sx={{ width: '100%', overflowX: 'auto', pb: 2 }}>
                <Box sx={{ height: 350, width: '100%', minWidth: isLoading ? '100%' : chartMinWidth }}>
                    {isLoading ? (
                        <GenericChartSkeleton height="350px" />
                    ) : (
                        <Bar options={topChartOptions} data={topChartData} plugins={[dataLabelPlugin]} />
                    )}
                </Box>
            </Box>
        </Box >
    );
});

export default ActiveProjectsSalesRepChart;
